import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import YoutubePreview from './YoutubePreview';
import IframePreview from './IframePreview';
import BlogImageWithCaption from '../BlogImageWithCaption';
import BlogVideoWithCaption from '../BlogVideoWithCaption';
import Code from '../Code';

const sanitizeConfig = {
  tagNames: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'a',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'hr',
    'iframe',
    'span',
    'strong',
    'em',
    'del',
  ],
  attributes: {
    a: ['href', 'title', 'target', 'rel'],
    img: ['src', 'alt', 'title'],
    iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title'],
    code: ['class', 'className', 'node'],
    pre: ['class', 'className'],
    table: ['class'],
    th: ['class'],
    td: ['class'],
    tr: ['class'],
    thead: ['class'],
    tbody: ['class'],
    span: ['class'],
  },
  protocols: {
    src: ['http', 'https', 'data'],
    href: ['http', 'https', 'mailto', 'tel'],
  },
  clobber: [],
  clobberPrefix: '',
  allowComments: true,
  allowElements: ['span', 'code', 'pre'],
  allowCustomElements: true,
  classesAttributes: ['class', 'className', 'hljs', 'language-*'],
};

const MarkdownRenderer = ({post}) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeConfig], rehypeHighlight]}
        components={{
          h1: ({children}) => <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>,
          h2: ({children}) => <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>,
          h3: ({children}) => <h3 className="text-xl font-bold mt-5 mb-2">{children}</h3>,
          h4: ({children}) => <h4 className="text-lg font-bold mt-4 mb-2">{children}</h4>,
          h5: ({children}) => <h5 className="text-base font-bold mt-3 mb-1">{children}</h5>,
          h6: ({children}) => <h6 className="text-sm font-bold mt-3 mb-1">{children}</h6>,
          p: ({children, ...props}) => (
            <p className="my-4" {...props}>
              {children}
            </p>
          ),
          strong: ({children}) => <strong className="font-bold">{children}</strong>,
          em: ({children}) => <em className="italic">{children}</em>,
          del: ({children}) => (
            <del className="line-through text-gray-500 dark:text-gray-400">{children}</del>
          ),
          a: ({children, ...props}) => (
            <a className="text-blue-600 hover:underline" {...props}>
              {children}
            </a>
          ),
          ul: ({children}) => <ul className="list-disc pl-6 my-4">{children}</ul>,
          ol: ({children}) => <ol className="list-decimal pl-6 my-4">{children}</ol>,
          li: ({children}) => <li className="mb-1">{children}</li>,
          blockquote: ({children}) => (
            <blockquote className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 italic my-4">
              {children}
            </blockquote>
          ),
          code: ({children, className, ...props}) => {
            const match = /language-(\w+)/.exec(className || '');

            return match ? (
              <pre className="bg-gray-100 dark:bg-gray-800 rounded p-4 overflow-x-auto my-4">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code
                className="bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 rounded px-1 py-0.5 inline-block"
                {...props}
              >
                {children}
              </code>
            );
          },
          img: ({src, alt}) => (
            <BlogImageWithCaption caption={alt} alt={alt || 'Image'} src={src} />
          ),
          table: ({children}) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                {children}
              </table>
            </div>
          ),
          thead: ({children}) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
          tbody: ({children}) => (
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">{children}</tbody>
          ),
          tr: ({children}) => <tr>{children}</tr>,
          th: ({children}) => <th className="px-4 py-2 text-left font-medium">{children}</th>,
          td: ({children}) => <td className="px-4 py-2">{children}</td>,
          hr: () => <hr className="my-6 border-gray-300 dark:border-gray-700" />,
          iframe: ({node, ...props}) => {
            return (
              <IframePreview
                node={{
                  url: props.src,
                }}
                {...props}
              />
            );
          },
        }}
      >
        {post}
      </ReactMarkdown>
    </>
  );
};

export default MarkdownRenderer;
